// Carga masiva de extras (bono/descuento) por Excel. Flujo: descargar template →
// subir → parsear (SheetJS en el cliente) → resolver correos→repartidor (scopeado al
// operador) → validar TODO-O-NADA → preview → aplicar. El backend crea un lote
// (batchId) rollbackeable. Monto con signo: positivo = bono, negativo = descuento.
import React, { useMemo, useState } from 'react';
import { toast } from '../../../utils/toast';
import { useTranslation } from 'react-i18next';
import { read, utils, writeFile } from 'xlsx';
import { Download, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Modal } from '../../../components/ui/Modal';
import { resolveDeliveryEmails } from '../../../api/user';
import { addDriverExtrasBatch } from '../../../api/billing';
import { formatCurrency } from './utils/cobroHelpers';

const pad = (n) => String(n).padStart(2, '0');

// Normaliza una celda de fecha a 'YYYY-MM-DD'. Acepta Date (celda de fecha de Excel),
// 'YYYY-MM-DD', y 'DD-MM-YYYY' / 'DD/MM/YYYY' (formato chileno). null = inválida.
const parseDateCell = (v) => {
  if (v == null || v === '') return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) {
    return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`;
  }
  const s = String(v).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return `${m[3]}-${pad(+m[2])}-${pad(+m[1])}`;
  return null;
};

// Monto en pesos enteros con signo. Trata cualquier no-dígito como separador de miles
// ("$5.000" → 5000, "-1.234" → -1234). Los pesos no llevan decimales.
const parseAmount = (v) => {
  if (v == null) return NaN;
  const s = String(v).trim();
  if (!s) return NaN;
  const neg = /^-/.test(s);
  const digits = s.replace(/[^\d]/g, '');
  if (!digits) return NaN;
  return (neg ? -1 : 1) * Number(digits);
};

export default function BulkExtrasModal({ open, onClose, onApplied }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [filename, setFilename] = useState('');
  const [parsing, setParsing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setRows([]);
    setFilename('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const downloadTemplate = () => {
    const ws = utils.aoa_to_sheet([
      ['Fecha', 'Correo', 'Monto', 'Motivo'],
      ['2026-07-06', 'repartidor@ejemplo.com', 5000, 'Bono puntualidad'],
      ['2026-07-06', 'otro@ejemplo.com', -3000, 'Descuento por faltante'],
    ]);
    ws['!cols'] = [{ wch: 12 }, { wch: 30 }, { wch: 12 }, { wch: 32 }];
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Extras');
    writeFile(wb, 'template-extras-repartidores.xlsx');
  };

  const handleFile = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const buf = await file.arrayBuffer();
      const wb = read(buf, { type: 'array', cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      // raw: true entrega el valor subyacente: las celdas de fecha (numFmtId de fecha)
      // llegan como Date —que parseDateCell maneja— y los montos como número. Con
      // raw:false SheetJS devolvía el texto cacheado del formato del cliente (p.ej.
      // 'm/d/yy' de numFmtId 14, con año de 2 dígitos) y el dateNF no re-formatea ese
      // .w ya cacheado → parseDateCell lo rechazaba y toda fila caía en "fecha inválida".
      const aoa = utils.sheet_to_json(sheet, {
        header: 1,
        raw: true,
        defval: '',
      });
      const dataRows = aoa
        .slice(1)
        .filter((r) => Array.isArray(r) && r.some((c) => String(c ?? '').trim() !== ''));

      if (dataRows.length === 0) {
        toast.error(t('El archivo no tiene filas de datos'));
        setParsing(false);
        return;
      }

      const parsed = dataRows.map((r, i) => ({
        rowNum: i + 2, // fila de Excel (1-based + encabezado)
        date: parseDateCell(r[0]),
        email: String(r[1] ?? '').trim(),
        amount: parseAmount(r[2]),
        description: String(r[3] ?? '').trim(),
      }));

      // Resolver correos → repartidores del operador (scopeado por el JWT en el backend).
      const emails = Array.from(new Set(parsed.map((p) => p.email).filter(Boolean)));
      let map = {};
      try {
        const res = await resolveDeliveryEmails(emails);
        (res?.data ?? []).forEach((u) => {
          if (u?.email) map[String(u.email).toLowerCase()] = u;
        });
      } catch (err) {
        toast.error(err.response?.data?.message ?? t('No se pudieron resolver los correos'));
        setParsing(false);
        return;
      }

      const validated = parsed.map((p) => {
        const errors = [];
        if (!p.date) errors.push(t('fecha inválida'));
        if (!p.email) errors.push(t('correo vacío'));
        const u = p.email ? map[p.email.toLowerCase()] : null;
        if (p.email && !u) errors.push(t('correo no es repartidor del operador'));
        if (!Number.isFinite(p.amount) || Math.round(p.amount) === 0) {
          errors.push(t('monto inválido (≠ 0)'));
        }
        if (!p.description) errors.push(t('motivo vacío'));
        return {
          ...p,
          driverId: u?.driverId ?? null,
          driverName: u?.name ?? null,
          errors,
        };
      });

      setRows(validated);
      setFilename(file.name);
    } catch (err) {
      toast.error(t('No se pudo leer el archivo (¿es un Excel válido?)'));
    } finally {
      setParsing(false);
    }
  };

  const { errorCount, validCount, totalBono, totalDescuento } = useMemo(() => {
    let errorCount = 0;
    let validCount = 0;
    let totalBono = 0;
    let totalDescuento = 0;
    for (const r of rows) {
      if (r.errors.length) errorCount += 1;
      else {
        validCount += 1;
        if (r.amount > 0) totalBono += r.amount;
        else totalDescuento += r.amount;
      }
    }
    return { errorCount, validCount, totalBono, totalDescuento };
  }, [rows]);

  const canApply = rows.length > 0 && errorCount === 0;

  const apply = async () => {
    if (!canApply) {
      toast.error(t('Hay filas con error; corrige el archivo (es todo o nada)'));
      return;
    }
    setSubmitting(true);
    try {
      const items = rows.map((r) => ({
        driverId: r.driverId,
        date: r.date,
        amount: Math.round(r.amount),
        description: r.description,
      }));
      const res = await addDriverExtrasBatch({ filename, items });
      toast.success(t('Lote aplicado: {{n}} extras', { n: res?.data?.applied ?? items.length }));
      onApplied?.();
      close();
    } catch (err) {
      toast.error(err.response?.data?.message ?? t('No se pudo aplicar el lote'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={t('Carga masiva de extras')}
      footer={
        <>
          <Button variant="outline" onClick={close}>{t('Cerrar')}</Button>
          <Button onClick={apply} disabled={!canApply} loading={submitting}>
            {t('Aplicar lote')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-fg-muted">
          {t('Sube un Excel con columnas Fecha · Correo · Monto · Motivo. El correo identifica al repartidor. Monto positivo = bono, negativo = descuento. Si alguna fila tiene error, no se carga nada (todo o nada).')}
        </p>

        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate} iconLeft={<Download className="w-4 h-4" />}>
            {t('Descargar template')}
          </Button>
          <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-bg text-sm cursor-pointer hover:bg-bg-subtle">
            <Upload className="w-4 h-4" />
            {parsing ? t('Leyendo…') : t('Subir Excel')}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              disabled={parsing}
              onChange={(e) => {
                handleFile(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          {filename && <span className="text-xs text-fg-muted truncate">{filename}</span>}
        </div>

        {rows.length > 0 && (
          <>
            <div className="flex items-center gap-4 flex-wrap text-sm">
              {errorCount === 0 ? (
                <span className="inline-flex items-center gap-1 text-status-success">
                  <CheckCircle2 className="w-4 h-4" /> {t('{{n}} filas válidas', { n: validCount })}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-status-error">
                  <AlertTriangle className="w-4 h-4" /> {t('{{e}} con error de {{n}} filas', { e: errorCount, n: rows.length })}
                </span>
              )}
              {errorCount === 0 && (
                <span className="text-fg-muted">
                  {t('Bonos')}: {formatCurrency(totalBono)} · {t('Descuentos')}: {formatCurrency(totalDescuento)}
                </span>
              )}
            </div>

            <div className="max-h-80 overflow-auto border border-border rounded-md">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-bg-muted">
                  <tr className="text-fg-subtle text-xs text-left">
                    <th className="py-1.5 px-2">#</th>
                    <th className="py-1.5 px-2">{t('Fecha')}</th>
                    <th className="py-1.5 px-2">{t('Repartidor')}</th>
                    <th className="py-1.5 px-2">{t('Monto')}</th>
                    <th className="py-1.5 px-2">{t('Motivo')}</th>
                    <th className="py-1.5 px-2">{t('Estado')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.rowNum} className={`border-t border-border/60 ${r.errors.length ? 'bg-status-error/5' : ''}`}>
                      <td className="py-1.5 px-2 text-fg-subtle">{r.rowNum}</td>
                      <td className="py-1.5 px-2 text-fg">{r.date || '—'}</td>
                      <td className="py-1.5 px-2 text-fg">
                        {r.driverName || <span className="text-fg-muted">{r.email || '—'}</span>}
                      </td>
                      <td className={`py-1.5 px-2 font-semibold ${r.amount < 0 ? 'text-status-error' : 'text-status-success'}`}>
                        {Number.isFinite(r.amount) ? `${r.amount > 0 ? '+' : ''}${formatCurrency(r.amount)}` : '—'}
                      </td>
                      <td className="py-1.5 px-2 text-fg-muted truncate" style={{ maxWidth: 180 }}>{r.description || '—'}</td>
                      <td className="py-1.5 px-2">
                        {r.errors.length ? (
                          <span className="text-xs text-status-error">{r.errors.join(', ')}</span>
                        ) : (
                          <span className="text-xs text-status-success">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
